import {
  Avatar,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  TableTagElectricChargeType,
  TableTagRefundStatus,
  sortByDateTime,
  usePersistMuiTableState,
  useUrlTableFilter,
} from "@qivia/ui";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { documentDownloadXlsxAsync } from "../../../libSlice/downloadFileXslx";
import {
  formatCurrency,
  formatDateTime,
  formatHour,
  getDateTime,
} from "../../../libSlice/materialUiFormats";
import {
  getElectricChargesAsync,
  selectElectricCharges,
  selectElectricChargesStatus,
} from "./electricChargingSlice";
import { ElectricChargeDisplayed } from "./electricChargingAPI";
import "../../../i18n/i18n";

export const ChargingTrackingTab = () => {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useAppDispatch();
  const electricCharges = useAppSelector(selectElectricCharges);
  const electricChargesStatus = useAppSelector(selectElectricChargesStatus);
  const [rowHoverUuid, setRowHoverUuid] = useState<string | null>(null);
  const [listDisplayed, setListDisplayed] = useState<
    ElectricChargeDisplayed[] | null
  >(null);

  const pageName = "electric_charging";
  const apiRef = useGridApiRef();
  const dataGridState = usePersistMuiTableState(
    apiRef,
    "customers-grid",
    pageName,
  );

  useEffect(() => {
    if (electricChargesStatus === "idle") {
      void dispatch(getElectricChargesAsync());
    }
  }, [dispatch, electricChargesStatus]);

  const findKeysToTranslate = useCallback(
    (key: keyof ElectricChargeDisplayed, value: string) => {
      switch (key) {
        case "type":
          return value ? t(`electric_charging.type.${value}`) : value;
        case "startAt":
        case "stopAt":
          return formatDateTime(new Date(value));
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () => ["type", "startAt", "stopAt"] as (keyof ElectricChargeDisplayed)[],
    [],
  );

  const allChargesDisplayed: ElectricChargeDisplayed[] = useMemo(() => {
    const listSorted = sortByDateTime(electricCharges, "desc", "startAt");
    let rowsWithId = listSorted.map((charge) => {
      const newDict: ElectricChargeDisplayed & { id: string } = {
        ...charge,
        id: charge.uuid,
      };
      return newDict;
    });

    keysToTranslate.forEach((key) => {
      rowsWithId = rowsWithId.map((row) => {
        return {
          ...row,
          [key]: findKeysToTranslate(key, row[key] as string),
        };
      });
    });

    return rowsWithId;
  }, [electricCharges, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (electricChargesStatus === "success") {
      setListDisplayed(allChargesDisplayed);
    }
  }, [allChargesDisplayed, electricChargesStatus]);

  const [searchParams] = useSearchParams();

  useUrlTableFilter(apiRef, !!listDisplayed, {
    value: searchParams.get("filterStatus"),
    field: "refundStatus",
    filterId: "refundStatusUrlFilter",
  });

  const searchBarProps = {
    values: allChargesDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["id" as const],
  };

  const columnsDisplayed: {
    [index in keyof ElectricChargeDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      registrationNumber: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => value || "-",
      },
      type: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) =>
          value ? (
            <StyledRow>
              <TableTagElectricChargeType type={value} />
            </StyledRow>
          ) : (
            "-"
          ),
      },
      identifier: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => value || "-",
      },
      vehicleReference: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => value || "-",
      },
      driverName: {
        type: "string",
        width: 250,
        valueFormatter: (value: string, row) => {
          if (!value) return <>-</>;
          const initials = value.split(" ").map((word) => word.charAt(0));
          return (
            <StyledRow>
              <Avatar
                color={"mint"}
                size={"S"}
                firstName={initials[0]}
                lastName={initials[initials.length - 1]}
                isHover={rowHoverUuid === row["id"]}
              />
              <Spacer x={0.75} />
              {value}
            </StyledRow>
          );
        },
      },
      startAt: {
        type: "date",
        width: 200,
        valueGetter: getDateTime,
        valueFormatter: formatDateTime,
      },
      stopAt: {
        type: "date",
        width: 200,
        valueGetter: getDateTime,
        valueFormatter: formatDateTime,
      },
      locationCity: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => value || "-",
      },
      totalCost: {
        type: "number",
        width: 160,
        valueFormatter: (value: number) => formatCurrency(value ?? 0),
      },
      refundAmount: {
        type: "number",
        width: 220,
        valueFormatter: (value: number) =>
          value != null ? formatCurrency(value) : "-",
      },
      refundStatus: {
        type: "string",
        width: 220,
        valueFormatter: (value: string) =>
          value ? (
            <StyledRow>
              <TableTagRefundStatus status={value} />
            </StyledRow>
          ) : (
            "-"
          ),
      },
      totalTime: {
        type: "number",
        width: 160,
        valueFormatter: (value: number) => formatHour(value) || "-",
      },
      totalEnergy: {
        type: "number",
        width: 200,
      },
      chargingTime: {
        type: "number",
        width: 160,
        valueFormatter: (value: number) => formatHour(value) || "-",
      },
      totalParkingTime: {
        type: "number",
        width: 190,
        valueFormatter: (value: number) => formatHour(value) || "-",
      },
    };
  }, [rowHoverUuid]);

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("electric_charging.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  if (params.tab !== "charging_tracking") {
    return null;
  }

  if (!listDisplayed) {
    return <LaunchPage hasBorderRadius={true} />;
  }

  return (
    <MaterialUiTable<keyof ElectricChargeDisplayed, ElectricChargeDisplayed>
      rows={listDisplayed}
      columns={columnsDisplayed}
      pageName={pageName}
      setRowHoverUuid={setRowHoverUuid}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
      downloadViewAction={downloadViewAction}
    />
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
